/**
 * TodosMode Component - Scrollable todo list within Mantine AppShell
 * 
 * Key solution elements:
 * 1. CSS container with calculated height: calc(100vh - header height)
 * 2. Responsive breakpoint at 768px for mobile/desktop layouts
 * 3. Mantine ScrollArea with CSS flex: 1 for proper scrolling behavior
 * 4. Fixed SuperTaskToolbar at top, scrollable TodoList below
 * 
 * CSS Classes:
 * - .todos-mode-container: Main responsive container
 * - .todos-toolbar-section: Fixed toolbar (flex-shrink: 0)
 * - .todos-scroll-section: Scrollable area (flex: 1)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useSearchParams } from 'react-router-dom';
import { TodoList } from '../TodoList';
import { EisenhowerMatrix } from './EisenhowerMatrix';
import { SuperTaskToolbar } from '../SuperTaskToolbar';
import { useListManagement } from '../../hooks/useListManagement';
import { ListManagementModals } from '../ListManagementModals';
import { usePersistedState } from '../../hooks/usePersistedState';
import { useTodoLists } from '../../hooks/useTodos';
import { useTodoFilter } from '../../contexts/TodoFilterContext';
import './TodosMode.css';

export const TodosMode: React.FC = () => {
  const { data: todoLists = [] } = useTodoLists();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setCurrentListId } = useTodoFilter();
  
  // View mode state - persisted
  const [viewMode, setViewMode] = usePersistedState<'list' | 'matrix'>('symphonytodo-view-mode', 'list');
  
  // Todo filters state
  const [todoFilters, setTodoFilters] = usePersistedState<{ assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }>('symphonytodo-filters', {
    assignee: 'any',
    list: 'all',
    completed: 'all' as 'today' | 'all' | 'hidden',
    tags: [] as string[],
    search: ''
  });

  // Update global context when list filter changes
  useEffect(() => {
    setCurrentListId(todoFilters.list);
  }, [todoFilters.list, setCurrentListId]);

  // Cleanup highlight timer on unmount
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  // Highlighting state for task navigation
  const [highlightedTaskId, setHighlightedTaskId] = useState<number|null>(null);
  const [highlightMessage, setHighlightMessage] = useState<string|null>(null);
  const [pendingHighlight, setPendingHighlight] = useState<{taskId: number, message: string} | null>(null);
  
  // Timer ref to track highlight timeout and reset it when new items are added
  const highlightTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // List management with centralized hook
  const listManagement = useListManagement({
    onListCreated: (newList) => {
      // Auto-select newly created list
      setTodoFilters(f => ({ ...f, list: newList.id }));
    },
    onListDeleted: () => {
      // Reset filters to show all lists after deletion
      setTodoFilters({ ...todoFilters, list: 'all' });
    },
  });

  // Compute selected list
  const selectedList = todoLists.find(list => list.id === todoFilters.list) || null;

  // Handle filter changes
  const handleFilterChange = (newFilters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => {
    setTodoFilters(newFilters);
    
    // Clear highlight and timer
    setHighlightedTaskId(null);
    setHighlightMessage(null);
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
  };

  // Handle list editing
  const handleEditList = () => {
    if (selectedList) {
      listManagement.handleEditList(selectedList);
    }
  };

  // Handle create list
  const handleCreateList = () => {
    listManagement.handleCreateList();
  };

  // Handle todo updates (like due date changes) with highlighting
  const handleTodoUpdate = (taskId: number, message?: string) => {
    // Set pending highlight - this will be applied when data updates
    setPendingHighlight({ taskId, message: message || 'Task updated' });
  };

  // Function to apply pending highlight and clear it
  const applyPendingHighlight = () => {
    if (pendingHighlight) {
      setHighlightedTaskId(pendingHighlight.taskId);
      setHighlightMessage(pendingHighlight.message);
      setPendingHighlight(null);
      
      // Clear any existing highlight timer to reset the duration
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      
      // Set new highlight timer for full 5 seconds
      highlightTimerRef.current = setTimeout(() => {
        setHighlightedTaskId(null);
        setHighlightMessage(null);
        highlightTimerRef.current = null;
      }, 5000);
    }
  };

  // Handle notification-based task highlighting via URL parameters
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    const shouldHighlight = searchParams.get('highlight') === 'true';
    const listId = searchParams.get('listId');
    const assignee = searchParams.get('assignee');
    const customHighlightMessage = searchParams.get('highlightMessage');
    
    if (taskId && shouldHighlight) {
      const taskIdNumber = parseInt(taskId, 10);
      const listIdNumber = listId ? parseInt(listId, 10) : null;
      
      if (!isNaN(taskIdNumber)) {
        // First, update filters to ensure the task is visible
        const newFilters = {
          list: listIdNumber || 'all',
          assignee: assignee || 'any',
          completed: 'all' as const, // Show all tasks including completed ones
          tags: [],
          search: ''
        };
        
        setTodoFilters(newFilters);
        
        // Set the task to be highlighted with custom message
        setHighlightedTaskId(taskIdNumber);
        setHighlightMessage(customHighlightMessage || 'Task from notification');
        
        // Clear the URL parameters
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('taskId');
        newSearchParams.delete('highlight');
        newSearchParams.delete('highlightMessage');
        newSearchParams.delete('listId');
        newSearchParams.delete('assignee');
        setSearchParams(newSearchParams, { replace: true });
        
        // Clear any existing highlight timer to reset the duration
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current);
        }
        
        // Set new highlight timer for full 5 seconds
        highlightTimerRef.current = setTimeout(() => {
          setHighlightedTaskId(null);
          setHighlightMessage(null);
          highlightTimerRef.current = null;
        }, 5000);
      }
    }
  }, [searchParams, setSearchParams, setTodoFilters]);

  // Handle incoming navigation that sets only the listId (e.g., from Document legend click)
  useEffect(() => {
    const listId = searchParams.get('listId');
    if (listId && !searchParams.get('taskId') && !searchParams.get('highlight')) {
      const listIdNumber = parseInt(listId, 10);
      if (!isNaN(listIdNumber)) {
        setTodoFilters(prev => ({ ...prev, list: listIdNumber }));
        // Clean the URL param after applying
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('listId');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, setTodoFilters]);

  return (
    <div className="todos-mode-container">
      <Routes>
        <Route 
          path="*" 
          element={
            <>
              <div className="todos-toolbar-section">
                <SuperTaskToolbar
                  onFilterChange={handleFilterChange}
                  currentFilters={todoFilters}
                  selectedList={selectedList}
                  onCreateList={handleCreateList}
                  onEditList={handleEditList}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                />
              </div>
              {/* Scrollable area that takes remaining space */}
              <div className="todos-scroll-section">
                {viewMode === 'list' ? (
                  <TodoList 
                    filters={todoFilters}
                    onFiltersChange={handleFilterChange}
                    highlightedTaskId={highlightedTaskId}
                    onTaskInteraction={() => {
                      setHighlightedTaskId(null);
                      setHighlightMessage(null);
                      // Clear timer when user interacts with task
                      if (highlightTimerRef.current) {
                        clearTimeout(highlightTimerRef.current);
                        highlightTimerRef.current = null;
                      }
                    }}
                    highlightMessage={highlightMessage || undefined}
                    onTodoUpdate={handleTodoUpdate}
                    pendingHighlight={pendingHighlight}
                    onApplyPendingHighlight={applyPendingHighlight}
                    hideToolbar={true}
                  />
                ) : (
                  <EisenhowerMatrix filters={todoFilters} />
                )}
              </div>
            </>
          } 
        />
      </Routes>

      {/* Centralized List Management Modals */}
      <ListManagementModals listManagement={listManagement} />
    </div>
  );
};
